import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useEffect } from 'react';
import { useSEO, generateLocalBusinessSchema, generateWebPageSchema, generateServiceSchema, generateBreadcrumbSchema } from '../../utils/seo';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import BookingChoiceModal from '../../components/feature/BookingChoiceModal';

export default function ServicesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // SEO Configuration
  useSEO({
    title: "Landscaping Services Harford County MD - Lawn Care, Hardscaping & Property Management",
    description: "Complete landscaping services in Harford County, Maryland including lawn care, mulching, hardscaping, tree services, power washing, fence installation, and full property management. Licensed and insured contractors with 15+ years experience.",
    keywords: "landscaping services Harford County, lawn care Maryland, mulching services, hardscaping, tree services, power washing, fence installation, property management services",
    canonical: "/services",
    ogImage: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/og-image.jpg`,
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema(),
        generateWebPageSchema(
          "Landscaping Services Harford County MD - Lawn Care, Hardscaping & Property Management",
          "Complete landscaping services in Harford County, Maryland including lawn care, mulching, hardscaping, tree services, power washing, fence installation, and full property management.",
          "/services"
        ),
        generateServiceSchema(
          "Landscaping Services",
          "Professional landscaping services including lawn care, mulching, hardscaping, tree services, and complete property management in Harford County, Maryland."
        ),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Services', url: '/services' }
        ])
      ]
    }
  });

  const [showZipcodeModal, setShowZipcodeModal] = useState(false);
  const [zipcode, setZipcode] = useState('');
  const [zipcodeStatus, setZipcodeStatus] = useState<'idle' | 'checking' | 'qualified' | 'not-qualified'>('idle');
  const [showBookingChoice, setShowBookingChoice] = useState(false);

  const qualifiedZipcodes = ['21001', '21005', '21009', '21010', '21014', '21015', '21017', '21028', '21034', '21040', '21047', '21050', '21051', '21078', '21082', '21084', '21085', '21087', '21111', '21120', '21128', '21131', '21132', '21154', '21156', '21160', '21161'];

  const handleGetQuote = () => {
    setShowZipcodeModal(true);
    setZipcode('');
    setZipcodeStatus('idle');
  };

  const handleZipcodeCheck = () => {
    setZipcodeStatus('checking');
    setTimeout(() => {
      if (qualifiedZipcodes.includes(zipcode)) {
        setZipcodeStatus('qualified');
      } else {
        setZipcodeStatus('not-qualified');
      }
    }, 800);
  };

  const handleContinueToBooking = () => {
    window.REACT_APP_NAVIGATE('/qualified-booking');
    setShowZipcodeModal(false);
  };

  const handleContactUs = () => {
    setShowZipcodeModal(false);
    const footer = document.querySelector('footer');
    if (footer) {
      footer.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const bookingChoiceServices = ["Mulching & Gardening", "Lawn Mowing & Maintenance", "Power Washing Services"];

  const coreServices = [
    {
      title: "Mulching & Gardening",
      description: "Professional mulching services, garden design, plant installation, and seasonal garden maintenance to keep your landscape beautiful year-round.",
      image: "https://static.readdy.ai/image/c82a80d6d696663c2db3260ddbcff61b/1372d4f25c2f636f63a0df071af89e47.png",
      features: ["Premium mulch installation", "Garden design & planning", "Plant selection & installation", "Seasonal maintenance", "Soil preparation", "Weed control"]
    },
    {
      title: "Lawn Mowing & Maintenance",
      description: "Complete lawn care services including regular mowing, edging, trimming, and seasonal maintenance to keep your grass healthy and pristine.",
      image: "https://static.readdy.ai/image/c82a80d6d696663c2db3260ddbcff61b/275aa04d8ae8278dfcbe6fc3b084f978.jpeg", 
      features: ["Weekly/bi-weekly mowing", "Professional edging", "Trimming & pruning", "Leaf removal", "Fertilization programs", "Aeration services"],
      link: "/ai-lawncare"
    },
    {
      title: "Power Washing Services",
      description: "Professional soft washing and power washing for driveways, decks, siding, patios, and more. We restore surfaces to like-new condition.",
      image: "https://static.readdy.ai/image/c82a80d6d696663c2db3260ddbcff61b/ed7183572657a5be75e5acd96d2db50c.png",
      features: ["Driveway cleaning", "Deck restoration", "Siding washing", "Patio cleaning", "Fence cleaning", "Gutter cleaning"]
    },
    {
      title: "Tree Services & Installation",
      description: "Complete tree care including planting, removal, trimming, and health assessments. We also install new trees and provide ongoing care.",
      image: "https://static.readdy.ai/image/c82a80d6d696663c2db3260ddbcff61b/8ca8d5a6bf968fbbe362e30d0f6d3428.png",
      features: ["Tree planting", "Tree removal", "Pruning & trimming", "Stump grinding", "Tree health assessment", "Emergency tree service"]
    },
    {
      title: "Hardscaping & Installations", 
      description: "Custom hardscaping solutions including patios, walkways, retaining walls, and outdoor living spaces designed to enhance your property.",
      image: "https://public.readdy.ai/ai/img_res/f28d4087da212bf64ab6d63cf84d3eac.jpg",
      features: ["Patio installation", "Walkway construction", "Retaining walls", "Outdoor kitchens", "Fire pits & features", "Drainage solutions"]
    },
    {
      title: "Fence & Deck Installation",
      description: "Professional fence and deck installation services. We handle permits, design, and installation of quality fencing and decking solutions.",
      image: "https://static.readdy.ai/image/c82a80d6d696663c2db3260ddbcff61b/3f371125d49d94b16c5c2250cece02d4.png",
      features: ["Custom fence design", "Deck construction", "Permit handling", "Material selection", "Professional installation", "Maintenance services"]
    }
  ];

  return (
    <>
      <div className="min-h-screen bg-white">
        <Header />
        
        {/* Hero Section */}
        <section className="relative min-h-[70vh] flex items-center bg-cover bg-center bg-no-repeat pt-24 pb-20" style={{
          backgroundImage: `linear-gradient(rgba(36, 130, 12, 0.85), rgba(131, 179, 40, 0.85)), url('/services-hero.jpg')`
        }}>
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Complete Property Services
            </h1>
            <p className="text-2xl md:text-3xl text-white mb-10 max-w-4xl mx-auto font-light">
              From expert landscaping to complete project management. We handle everything so you don't have to
            </p>
            <button 
              onClick={() => setShowZipcodeModal(true)}
              className="bg-white text-[#24820C] px-10 py-5 rounded-full text-xl font-bold hover:bg-gray-100 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer shadow-2xl"
            >
              Request Your Free Evaluation
            </button>
          </div>
        </section>

        {/* Core Services */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-6">
                Our Core Landscaping Services
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Professional landscaping services with 9+ years of experience in Harford County
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {coreServices.map((service, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all border border-gray-100 group">
                  <div className="h-80 w-full overflow-hidden bg-gray-100">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-10">
                    <h3 className="text-3xl font-bold text-gray-900 mb-5">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mb-8 leading-relaxed text-lg">
                      {service.description}
                    </p>
                    <div className="grid grid-cols-1 gap-4 mb-10">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center bg-gray-50 rounded-lg px-4 py-3">
                          <div className="w-6 h-6 bg-[#83B328] rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                            <i className="ri-check-line text-white text-sm"></i>
                          </div>
                          <span className="text-gray-700 font-medium">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => {
                        if (bookingChoiceServices.includes(service.title)) {
                          setShowBookingChoice(true);
                        } else {
                          handleGetQuote();
                        }
                      }}
                      className="bg-[#83B328] text-white px-8 py-4 rounded-xl font-bold hover:bg-[#6d9620] transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center text-lg shadow-lg"
                    >
                      Get Free Quote
                      <i className="ri-arrow-right-line ml-2"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Customer Reviews Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-6">Customer Reviews</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                See what our customers say about our landscaping services
              </p>
            </div>

            <div className="max-w-6xl mx-auto">
              <iframe 
                className='lc_reviews_widget' 
                src='https://links.jslandscapingmd.com/reputation/widgets/review_widget/bcqvNaDtoQZ2YHc2At0F?widgetId=68ee6ea756ab4287e9fbdd25' 
                frameBorder='0' 
                scrolling='no' 
                style={{minWidth: '100%', width: '100%', minHeight: '800px'}}
              ></iframe>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-[#83B328] to-[#24820C] relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/10"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <h2 className="text-5xl font-bold text-white mb-8">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-green-100 mb-12 max-w-3xl mx-auto leading-relaxed">
              Experience the convenience of having one trusted partner handle all your property needs. 
              Request your free evaluation today and see why Harford County trusts J's Landscaping Services.
            </p>
            <div className="flex justify-center">
              <button 
                onClick={() => setShowZipcodeModal(true)}
                className="bg-white text-[#24820C] px-10 py-5 rounded-full text-xl font-bold hover:bg-gray-100 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer shadow-2xl"
              >
                Book Online Now
              </button>
            </div>
          </div>
        </section>

        {/* Zipcode Modal */}
        {showZipcodeModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
              <button 
                onClick={() => {
                  setShowZipcodeModal(false);
                  setZipcodeStatus('idle');
                  setZipcode('');
                }}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>

              {zipcodeStatus === 'idle' && (
                <>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Check Your Area</h3>
                  <p className="text-gray-600 mb-6">Enter your zipcode to see if we service your area</p>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={zipcode}
                    onChange={(e) => setZipcode(e.target.value)}
                    placeholder="Enter zipcode"
                    maxLength={5}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#83B328] focus:border-transparent text-lg mb-4"
                  />
                  <button
                    onClick={handleZipcodeCheck}
                    disabled={zipcode.length !== 5}
                    className="w-full bg-[#83B328] text-white px-6 py-3 rounded-lg text-lg font-semibold hover:bg-[#6d9620] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Check Availability
                  </button>
                </>
              )}

              {zipcodeStatus === 'checking' && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#83B328] mx-auto mb-4"></div>
                  <p className="text-gray-600">Checking your area...</p>
                </div>
              )}

              {zipcodeStatus === 'qualified' && (
                <div className="text-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-4xl text-green-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Great News!</h3>
                  <p className="text-gray-600 mb-6">We service your area. Continue to book your service.</p>
                  <button
                    onClick={handleContinueToBooking}
                    className="w-full bg-[#83B328] text-white px-6 py-3 rounded-lg text-lg font-semibold hover:bg-[#6d9620] transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Continue to Booking
                  </button>
                </div>
              )}

              {zipcodeStatus === 'not-qualified' && (
                <div className="text-center">
                  <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-close-line text-4xl text-red-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Not in Service Area</h3>
                  <p className="text-gray-600 mb-6">We don't currently service your area, but please call us at (443) 819-1521 to discuss options.</p>
                  <button
                    onClick={handleContactUs}
                    className="w-full bg-[#83B328] text-white px-6 py-3 rounded-lg text-lg font-semibold hover:bg-[#6d9620] transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Contact Us
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        <Footer />
      </div>

      <BookingChoiceModal isOpen={showBookingChoice} onClose={() => setShowBookingChoice(false)} />
    </>
  );
}
